/**
 * Connection keep-warm (docs/03 Appendix B).
 *
 * After 45 seconds of HTTP idleness the pooled connection is at risk of having
 * been closed by an intermediary, and the next request pays a fresh TCP and TLS
 * handshake. On this product the next request is very often `POST /orders`,
 * placed with a few seconds left on a 47-second window, so that handshake lands
 * exactly where there is no budget for it.
 *
 * `HEAD /time` is about 90 bytes and is the cheapest endpoint in the API. This
 * is **not** a poll: nothing is read from the response, the clock is corrected
 * for free from the `X-UD-Server-Time-Ms` header the client already reads, and
 * the timer only fires when the connection has actually gone quiet.
 *
 * `setInterval` is allowed in this file by the ESLint allowlist because
 * Appendix B has a row for it. It is still not used — an interval that fires
 * while a request is in flight would defeat the idleness test it exists to
 * respect.
 */

import type { HttpClient } from './client'
import { IDLE_KEEP_WARM_MS } from './client'

export interface KeepWarmOptions {
  client: HttpClient
  idleMs?: number
  setTimer?: (fn: () => void, ms: number) => number
  clearTimer?: (handle: number) => void
  /** Skip the ping while the tab is hidden — a backgrounded tab is not about
   *  to place an order, and its timers are throttled anyway. */
  isActive?: () => boolean
}

export class KeepWarm {
  readonly #client: HttpClient
  readonly #idleMs: number
  readonly #setTimer: (fn: () => void, ms: number) => number
  readonly #clearTimer: (handle: number) => void
  readonly #isActive: () => boolean

  #handle: number | null = null
  #running = false
  #pings = 0

  constructor(options: KeepWarmOptions) {
    this.#client = options.client
    this.#idleMs = options.idleMs ?? IDLE_KEEP_WARM_MS
    this.#setTimer = options.setTimer ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
    this.#clearTimer = options.clearTimer ?? ((h) => globalThis.clearTimeout(h))
    this.#isActive = options.isActive ?? (() => true)
  }

  get running(): boolean {
    return this.#running
  }

  get pings(): number {
    return this.#pings
  }

  start(): void {
    if (this.#running) return
    this.#running = true
    this.#arm(this.#idleMs)
  }

  stop(): void {
    this.#running = false
    if (this.#handle !== null) {
      this.#clearTimer(this.#handle)
      this.#handle = null
    }
  }

  #arm(delayMs: number): void {
    if (this.#handle !== null) this.#clearTimer(this.#handle)
    this.#handle = this.#setTimer(() => {
      this.#handle = null
      void this.#tick()
    }, Math.max(0, delayMs))
  }

  async #tick(): Promise<void> {
    if (!this.#running) return

    const idle = this.#client.idleMs
    if (idle < this.#idleMs) {
      // Something else used the connection; re-arm for the remaining idle time
      // rather than pinging a connection that is already warm.
      this.#arm(this.#idleMs - idle)
      return
    }

    if (this.#isActive()) {
      this.#pings++
      // A failed keep-warm is not an error the user needs: the next real
      // request will surface the same fault with a status code attached.
      await this.#client.head('/time').catch(() => {})
    }

    if (this.#running) this.#arm(this.#idleMs)
  }
}
